import { create } from 'zustand';
import * as flowbackApi from '@/api/requirementFlowback';
import type {
  FlowbackRecord,
  FlowbackStatsItem,
  FlowbackStatus,
  LaggingRequirement,
} from '@/api/requirementFlowback';

export interface FlowbackListParams {
  status?: FlowbackStatus;
  project_id?: number;
  initiator?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

interface FlowbackState {
  records: FlowbackRecord[];
  total: number;
  isLoading: boolean;
  error: string | null;
  listParams: FlowbackListParams;

  stats: FlowbackStatsItem[];
  statsDimension: 'subcategory' | 'initiator' | 'project';
  dateFrom: string;
  dateTo: string;
  isStatsLoading: boolean;

  lagging: LaggingRequirement[];
  laggingSet: Set<string>;
  isLaggingLoading: boolean;

  eligibilitySet: Set<string>;
  isEligibilityLoading: boolean;

  fetchList: (params?: FlowbackListParams) => Promise<void>;
  setListParams: (params: Partial<FlowbackListParams>) => void;
  fetchStats: (params?: {
    dimension?: 'subcategory' | 'initiator' | 'project';
    date_from?: string;
    date_to?: string;
  }) => Promise<void>;
  fetchLagging: (projectId: number, subcategory?: string) => Promise<void>;
  fetchEligibility: (projectId: number, subcategory?: string) => Promise<void>;
}

export const useFlowbackStore = create<FlowbackState>()((set, get) => ({
  records: [],
  total: 0,
  isLoading: false,
  error: null,
  listParams: { status: 'all', page: 1, page_size: 20 },

  stats: [],
  statsDimension: 'initiator',
  dateFrom: '',
  dateTo: '',
  isStatsLoading: false,

  lagging: [],
  laggingSet: new Set<string>(),
  isLaggingLoading: false,

  eligibilitySet: new Set<string>(),
  isEligibilityLoading: false,

  fetchList: async (params) => {
    const merged = { ...get().listParams, ...params };
    set({ isLoading: true, error: null, listParams: merged });
    try {
      const res = await flowbackApi.listFlowback(merged);
      set({ records: res.results, total: res.count, isLoading: false });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      const message = err?.response?.data?.detail || '获取回流列表失败';
      set({ error: message, isLoading: false });
    }
  },

  setListParams: (params) => {
    set({ listParams: { ...get().listParams, ...params } });
  },

  fetchStats: async (params) => {
    set({ isStatsLoading: true });
    try {
      const dimension = params?.dimension ?? get().statsDimension;
      const date_from = params?.date_from ?? get().dateFrom;
      const date_to = params?.date_to ?? get().dateTo;
      const res = await flowbackApi.getFlowbackStats({ dimension, date_from, date_to });
      set({
        stats: res.items,
        statsDimension: dimension,
        dateFrom: date_from,
        dateTo: date_to,
        isStatsLoading: false,
      });
    } catch {
      set({ isStatsLoading: false });
    }
  },

  fetchLagging: async (projectId, subcategory) => {
    set({ isLaggingLoading: true });
    try {
      const res = await flowbackApi.getLagging(projectId, subcategory);
      set({
        lagging: res.items,
        laggingSet: new Set(res.items.map((i) => i.requirement_id)),
        isLaggingLoading: false,
      });
    } catch {
      set({ lagging: [], laggingSet: new Set(), isLaggingLoading: false });
    }
  },

  fetchEligibility: async (projectId, subcategory) => {
    set({ isEligibilityLoading: true });
    try {
      const res = await flowbackApi.getFlowbackEligibility(projectId, subcategory);
      set({
        eligibilitySet: new Set(res.items.map((i) => i.requirement_id)),
        isEligibilityLoading: false,
      });
    } catch {
      set({ eligibilitySet: new Set(), isEligibilityLoading: false });
    }
  },
}));
